import { useCallback, useMemo, useRef } from "react";

import {
  generatedV2,
  useProjectScopeReviewQuery,
  useV2DismissScopeReviewServiceMutation,
  useV2GenerateScopeReviewMutation,
  useV2UndismissScopeReviewServiceMutation,
  useV2UpdateScopeReviewMutation,
} from "@generated";
import { useDispatch } from "react-redux";
import { SaveResult, ScopeReview } from "../types";

// Every scope-review endpoint answers with this JSON:API envelope. The generated
// types are all-optional (GET) or `unknown` (mutations), and the v2 base query
// does not camelize keys, so attributes stay snake_case. Cast in one place.
interface ScopeReviewEnvelope {
  data?: { id?: string; type?: string; attributes?: ScopeReview };
}

const asEnvelope = (value: unknown): ScopeReviewEnvelope | undefined =>
  value && typeof value === "object" && "data" in value
    ? (value as ScopeReviewEnvelope)
    : undefined;

const errorStatus = (error: unknown): number | string | undefined =>
  (error as { status?: number | string } | undefined)?.status;

const NOT_FOUND_TITLE = "Scope review not found";

type SaveWaiter = (result: SaveResult) => void;

interface SaveQueue {
  inFlight: boolean;
  // The latest set asked for while a PATCH is in flight, plus every caller it covers.
  next: { ids: string[]; waiters: SaveWaiter[] } | null;
}

// Read through a function so TypeScript does not keep `next` narrowed to null
// across an await, during which other saves may have queued.
const queuedSave = (queue: SaveQueue) => queue.next;

export const useScopeReview = (projectId: string) => {
  const dispatch = useDispatch();
  const query = useProjectScopeReviewQuery(
    { id: projectId },
    { refetchOnMountOrArgChange: true }
  );
  const [generate, { isLoading: isRunning }] =
    useV2GenerateScopeReviewMutation();
  const [update] = useV2UpdateScopeReviewMutation();
  const [dismissService] = useV2DismissScopeReviewServiceMutation();
  const [undismissService] = useV2UndismissScopeReviewServiceMutation();

  const { data, error, isLoading, isError: queryErrored } = query;
  const refetch = query.refetch;

  const notReviewed = useMemo(() => {
    const body = (error as { data?: { errors?: { title?: string }[] } })?.data;
    return (
      errorStatus(error) === 404 && body?.errors?.[0]?.title === NOT_FOUND_TITLE
    );
  }, [error]);

  const review = useMemo<ScopeReview | null>(
    () => asEnvelope(data)?.data?.attributes ?? null,
    [data]
  );

  const writeCache = useCallback(
    (envelope: ScopeReviewEnvelope | undefined) => {
      if (!envelope?.data?.attributes) return false;
      dispatch(
        (generatedV2.util as any).upsertQueryData(
          "projectScopeReview",
          { id: projectId },
          envelope
        )
      );
      return true;
    },
    [dispatch, projectId]
  );

  const run = useCallback(async (): Promise<boolean> => {
    try {
      const response = await generate({ id: projectId }).unwrap();
      if (!writeCache(asEnvelope(response))) refetch();
      return true;
    } catch {
      return false;
    }
  }, [generate, projectId, writeCache, refetch]);

  // The save loop outlives renders, so read the review through a ref.
  const reviewRef = useRef(review);
  reviewRef.current = review;
  const saveQueue = useRef<SaveQueue>({ inFlight: false, next: null });

  const sendCompleted = useCallback(
    async (ids: string[]): Promise<SaveResult> => {
      const current = reviewRef.current;
      if (!current) return "error";
      try {
        await update({
          id: projectId,
          body: {
            data: {
              type: "scope-review",
              attributes: {
                generated_at: current.generated_at,
                completed_item_ids: ids,
              },
            },
          },
        }).unwrap();
        dispatch(
          (generatedV2.util as any).updateQueryData(
            "projectScopeReview",
            { id: projectId },
            (draft: ScopeReviewEnvelope) => {
              if (draft?.data?.attributes) {
                draft.data.attributes.completed_item_ids = ids;
                draft.data.attributes.generated_at = current.generated_at;
              }
            }
          )
        );
        return "ok";
      } catch (e) {
        if (errorStatus(e) === 409) {
          refetch();
          return "conflict";
        }
        return "error";
      }
    },
    [update, projectId, dispatch, refetch]
  );

  // Sends at most one PATCH at a time. Saves made meanwhile collapse into one
  // follow-up PATCH carrying the latest set, so the server ends on that set.
  const drainSaves = useCallback(async () => {
    const queue = saveQueue.current;
    if (queue.inFlight) return;
    queue.inFlight = true;
    // Callers whose PATCH failed but whose ids ride along in the next PATCH.
    let carried: SaveWaiter[] = [];
    for (
      let pending = queuedSave(queue);
      pending;
      pending = queuedSave(queue)
    ) {
      const { ids, waiters } = pending;
      queue.next = null;
      const covered = [...carried, ...waiters];
      carried = [];
      const result = await sendCompleted(ids);
      // Saves made during the await may have queued a follow-up.
      const queued = queuedSave(queue);
      if (result === "conflict") {
        // The review changed underneath us; the queued set is meaningless now.
        queue.next = null;
        [...covered, ...(queued?.waiters ?? [])].forEach((w) => w("conflict"));
      } else if (result === "error" && queued) {
        carried = covered;
      } else {
        covered.forEach((w) => w(result));
      }
    }
    queue.inFlight = false;
  }, [sendCompleted]);

  const saveCompleted = useCallback(
    (ids: string[]): Promise<SaveResult> => {
      if (!reviewRef.current) return Promise.resolve("error");
      return new Promise<SaveResult>((resolve) => {
        const queue = saveQueue.current;
        queue.next = {
          ids,
          waiters: [...(queue.next?.waiters ?? []), resolve],
        };
        void drainSaves();
      });
    },
    [drainSaves]
  );

  const dismiss = useCallback(
    async (taskId: number): Promise<boolean> => {
      try {
        const response = await dismissService({
          id: projectId,
          body: {
            data: {
              type: "dismissed-services",
              attributes: { task_id: taskId },
            },
          },
        }).unwrap();
        if (!writeCache(asEnvelope(response))) refetch();
        return true;
      } catch {
        return false;
      }
    },
    [dismissService, projectId, writeCache, refetch]
  );

  const undismiss = useCallback(
    async (taskId: number): Promise<boolean> => {
      try {
        const response = await undismissService({
          id: projectId,
          taskId,
        }).unwrap();
        if (!writeCache(asEnvelope(response))) refetch();
        return true;
      } catch {
        return false;
      }
    },
    [undismissService, projectId, writeCache, refetch]
  );

  return {
    review,
    notReviewed,
    isLoading,
    isError: queryErrored && !notReviewed,
    run,
    isRunning,
    saveCompleted,
    dismiss,
    undismiss,
    refetch: () => {
      refetch();
    },
  };
};

export default useScopeReview;
